import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import MarketingMediaView from "../components/home/MarketingMediaView";
import { Award, Briefcase, Calendar, Users } from "lucide-react";
import MarketingClientView from "../components/home/MarketingClientView";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import FooterView from "../components/home/layout/FooterView";
import FadeInOnScroll from "../components/common/FadeInOnScroll";
import { Variants } from "framer-motion";

const bannerVariants: Variants = {
  hidden: { opacity: 0, y: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 100, damping: 20 },
  },
};

interface MarketingInfoProps {
  imageUrls: string[];
  titles: string[];
  hrefs: string[];
}

interface MarketingClientInfoProps {
  imageUrls: string[];
}

interface MarketingPromotionInfoProps {
  imageUrls: string[];
}

const timelineData = [
  {
    period: "2025년",
    events: [
      {
        text: `(3년 연속) 관광바우처 제조혁신바우처 수출바우처 공식 수행 기관`,
        category: "achievement",
      },
      {
        text: `㈜세이프웨이 바이럴 마케팅 대행`,
        category: "client",
      },
      {
        text: `㈜트레이드월드 바이럴 마케팅 대행`,
        category: "client",
      },
      {
        text: `서울공예박물관 배리어프리 도슨트 웹페이지 제작`,
        category: "project",
      },
      {
        text: `한성기업(주) 와일드 크래미 디지털 광고`,
        category: "project",
      },
    ],
  },
  {
    period: "2024년",
    events: [
      {
        text: `SBA 서울경제진흥원 홍보영상 제작`,
        category: "project",
      },
      {
        text: `(3년 연속) 다우기술 퍼포먼스 마케팅 대행`,
        category: "achievement",
      },
      {
        text: `한성기업(주) 와일드 크래미 디지털 광고`,
        category: "project",
      },
      {
        text: `(3년 연속) 세방전지 로케트배터리 오프라인 프로모션 진행`,
        category: "achievement",
      },
      {
        text: `바른치킨 인플루언서 바이럴 마케팅 대행`,
        category: "client",
      },
      {
        text: `기웅정보통신(주) 퍼포먼스 마케팅 대행`,
        category: "client",
      },
      {
        text: `한국생산성본부 오프라인 판촉물 제작`,
        category: "project",
      },
      {
        text: `온유어사이드 인플루언서 바이럴 마케팅 대행`,
        category: "client",
      },
      {
        text: `페도라인 보행센터 인플루언서 바이럴 마케팅 대행`,
        category: "client",
      },
      {
        text: `바비온 인플루언서 공동구매 마케팅 대행`,
        category: "client",
      },
      {
        text: `㈜하이테커 라이브커머스 기획 및 대행`,
        category: "project",
      },
    ],
  },
  {
    period: "2023년",
    events: [
      {
        text: "네옴시티 글로벌 프로젝트 마케팅 대행",
        category: "project",
      },
      {
        text: `세방전지 로케트배터리 오프라인 프로모션 진행`,
        category: "project",
      },
      {
        text: "사이클룩스 CES 출품 모션그래픽 영상 제작",
        cateogry: "project",
      },
      {
        text: "대산미술관 모바일 APP 콘텐츠 제작",
        category: "project",
      },
      {
        text: "관광바우처 최다 진행 기업",
        category: "achievement",
      },
      {
        text: "라운드골프 SNS 운영",
        category: "client",
      },
      {
        text: "포포(PHO4) 브랜딩 대행",
        category: "client",
      },
      {
        text: "한국 외식 산업협회 온라인 마케팅 대행",
        category: "client",
      },
      {
        text: "프리미엄 가구 브랜드 아템포 온/오프라인 마케팅 대행",
        category: "client",
      },
    ],
  },
  {
    period: "2022년",
    events: [
      {
        text: `NH멤버스 앱 영상 컨텐츠 운영 대행(2년 연속)`,
        category: "achievement",
      },
      {
        text: "한국농수산식품유통공사 중소기업 라이브커머스 제작",
        category: "project",
      },
      {
        text: `세방 온/오프라인 통합 마케팅`,
        category: "client",
      },
    ],
  },
  {
    period: "2021년",
    events: [
      {
        text: "NH멤버스 앱 영상 컨텐츠 운영 대행",
        category: "project",
      },
      {
        text: "aT한국농수산식품유통공사 포스몰 라이브커머스 제작",
        category: "project",
      },
      {
        text: "강릉과학산업진흥원 라이브커머스 제작",
        category: "project",
      },
      {
        text: "강원경제진흥원 SNS 통합 마케팅",
        category: "client",
      },
      {
        text: "관광바우처 제조혁신바우처 서비스 제공 기업(2년 연속)",
        category: "achievement",
      },
    ],
  },
  {
    period: "2020년",
    events: [
      {
        text: "경희사이버대학교 온/오프라인 통합 마케팅(4년 연속)",
        category: "achievement",
      },
      { text: "제니하우스 코스메틱 마케팅 대행", category: "client" },
      { text: "팜스킨 마케팅 대행", category: "client" },
    ],
  },
  {
    period: "2019년",
    events: [
      {
        text: "이탈리아 프리미엄 가구 브랜드 전속 마케팅 대행",
        category: "client",
      },
      { text: "강원경제진흥원 온라인 검색광고 교육 대행", category: "project" },
      {
        text: "신전푸드시스 ‘정다율 짬뽕’ 지면 촬영 총괄 대행",
        category: "project ",
      },
    ],
  },
  {
    period: "2018년",
    events: [
      { text: "전주국제영화제 옥외광고 대행", category: "project" },
      { text: "DR. JAYJUN 신규 브랜드 런칭 행사 총괄", category: "project" },
    ],
  },
  {
    period: "2017년",
    events: [
      { text: "신전떡볶이 ATL 광고제작", category: "project" },
      { text: "33떡복이 프랜차이즈 브랜드 런칭", category: "project" },
      { text: "또봉이 통닭 온-오프라인 통합 마케팅", category: "client" },
      { text: "이니스프리 연간 마케팅(5년 연속)", category: "achievement" },
    ],
  },
  {
    period: "2016년",
    events: [
      { text: "벤처인증 및 기업부설연구소 설립", category: "milestone" },
      { text: "GS25울트라뮤직 페스티벌 프로모션", category: "project" },
    ],
  },
  {
    period: "2015년",
    events: [
      { text: "이니스프리 화산송이 미디어 플래닝", category: "project" },
      { text: "한국농수산식품유통공사(aT)홍보마케팅", category: "project" },
      { text: "우리카드 VIP 고객 프로모션", category: "project" },
    ],
  },
  {
    period: "2014년",
    events: [
      { text: "IBK기업은행 이벤트 마케팅", category: "client" },
      { text: "우리카드 온라인 컨텐츠 릴리즈 마케팅", category: "project" },
      { text: "호텔엔조이 제휴 프로모션", category: "project" },
      { text: "CGV 제휴 프로모션", category: "project" },
      { text: "아우디 참존 온라인 릴리즈 마케팅", category: "project" },
    ],
  },
  {
    period: "2013년",
    events: [
      { text: "우리카드 온라인 컨텐츠 마케팅", category: "client" },
      { text: "뉴발란스 뉴레이스 마라톤대회 프로모션", category: "project" },
      { text: "하나SK카드 웨딩 서비스 기획 및 운영", category: "project" },
    ],
  },
  {
    period: "2012년",
    events: [
      { text: "호텔엔조이 CGV공간 마케팅", category: "client" },
      { text: "IBK기업은행 CGV온라인 프로모션 광고", category: "project" },
      { text: "IBK기업은행-호텔엔조이 제휴 프로모션", category: "project" },
      { text: "롯데칠성음료-하나SK카드 제휴 마케팅", category: "project" },
      { text: "롯데칠성음로-SK TMC 제휴 마케팅", category: "project" },
      { text: "하나SK카드 웨딩 서비스 기획 및 운영", category: "project" },
    ],
  },
  {
    period: "2011년",
    events: [
      { text: "IBK하나은행 온라인 마케팅", category: "milestone" },
      { text: "호텔 엔조이 탐엔탐스 제휴 프로모션", category: "project" },
      { text: "하나SK카드 온·오프라인 연간 마케팅", category: "client" },
      { text: "KT-탐앤탐스-AE:A 제휴 프로모션", category: "project" },
    ],
  },
];

const MarketingInfo: MarketingInfoProps = {
  imageUrls: [
    "/assets/images/img/marketing/innisfree.png",
    "/assets/images/img/marketing/sinjeon.png",
    "/assets/images/img/marketing/jennyhouse.png",
    "/assets/images/img/marketing/hoteljoy.png",
    "/assets/images/img/marketing/paulbasset.png",
    "/assets/images/img/marketing/nhmember.png",
  ],
  titles: [
    "이니스프리",
    "신전떡볶이",
    "제니하우스",
    "호텔엔조이",
    "폴바셋",
    "NH멤버스",
  ],
  hrefs: [
    "https://www.youtube.com/embed/EAkce2JIS2U?autoplay=1&muted=1&rel=0",
    "https://www.youtube.com/embed/3-5nUXFN1VQ?autoplay=1&muted=1&rel=0",
    "https://www.youtube.com/embed/2X4Gq0YbMfY?autoplay=1&muted=1&rel=0",
    "https://www.youtube.com/embed/tsUGT_44ZN8?autoplay=1&muted=1&rel=0",
    "https://www.youtube.com/embed/rX-ubnebusQ?autoplay=1&muted=1&rel=0",
    "https://www.youtube.com/embed/bdl1ROkHCAY?autoplay=1&muted=1&rel=0",
  ],
};

const MarketingPromotionInfo: MarketingClientInfoProps = {
  imageUrls: [
    "/assets/images/img/marketing/innisfree.png",
    "/assets/images/img/marketing/sinjeon.png",
    "/assets/images/img/marketing/jennyhouse.png",
  ],
};

const MarketingClientInfo: MarketingClientInfoProps = {
  imageUrls: [
    "/assets/images/img/marketing/innisfree_logo.png", //1
    "/assets/images/img/marketing/sinjun_logo.png", // 2
    "/assets/images/img/marketing/hanacard_logo.png", //3
    "/assets/images/img/marketing/cgv_logo.png", // 4
    "/assets/images/img/marketing/nhmemember_logo.png", //5
    "/assets/images/img/marketing/gs25_logo.png", //6
    "/assets/images/img/marketing/cyberuniversity_logo.png", // 7
    "/assets/images/img/marketing/audi_logo.png", // 8
    "/assets/images/img/marketing/jennyhouse_logo.png", // 9
    "/assets/images/img/marketing/gangwon_logo.png", // 10
    "/assets/images/img/marketing/gangneung_logo.png", //11
    "/assets/images/img/marketing/koreafreash_logo.png", // 12
    "/assets/images/img/marketing/gangwondo_logo.png", //13
    "/assets/images/img/marketing/hanssem_logo.png", // 14
    "/assets/images/img/marketing/daou_logo.png", // 15
    "/assets/images/img/marketing/hoteljoy_logo.png", // 16
    "/assets/images/img/marketing/jayjun_logo.png", //17
    "/assets/images/img/marketing/33_logo.png", // 18
    "/assets/images/img/marketing/golf_logo.png", //19
    "/assets/images/img/marketing/sebang_logo.png", // 20
    "/assets/images/img/marketing/neom_logo.png", // 21
    "/assets/images/img/marketing/atempo_logo.png", // 22
    "/assets/images/img/marketing/musium_logo.png", // 23
  ],
};

function getCategoryIcon(category: string) {
  switch (category) {
    case "achievement":
      return <Award className="w-4 h-4" />;
    case "strategy":
      return <Calendar className="w-4 h-4" />;
    case "creativity":
      return <Users className="w-4 h-4" />;
    default:
      return <Briefcase className="w-4 h-4" />;
  }
}

function getCategoryColor(category: string) {
  switch (category) {
    case "achievement":
      return "bg-red-500 text-whiite";
    case "strategy":
      return "bg-gray-800 text-white";
    case "creativity":
      return "bg-gray-600 text-white";
    default:
      return "bg-gray-400 text-white";
  }
}

function getCategoryBolder(category: string) {
  switch (category) {
    case "achievement":
      return "border-red-500";
    case "strategy":
      return "border-gray-800";
    case "creativity":
      return "border-gray-600";
    default:
      return "border-gray-400";
  }
}

function getCategoryBackground(category: string) {
  switch (category) {
    case "achievement":
      return "bg-red-50";
    case "strategy":
      return "bg-gray-100";
    case "creativity":
      return "bg-gray-50";
    default:
      return "bg-gray-50";
  }
}

const Marketingpage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState("2025년");
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {}
  );
  const [animatedItems, setAnimatedItems] = useState(new Set());

  const toggleExpanded = (period: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [period]: !prev[period],
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedItems(new Set(timelineData.map((item) => item.period)));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const selectedData = timelineData.find(
    (item) => item.period === selectedYear
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const didShowMarketingContact = () => {
    navigate("/contact", { state: { type: "marketing" } });
  };
  const didShowIntroducePage = () => {
    navigate("/");
  };

  return (
    <div className="font-sans text-gray-800 bg-white">
      <section className="w-full relative overflow-hidden mt-[80px]">
        <video
          src="/assets/videos/marketing_banner.mp4"
          className="w-full aspect-[4/3] laptop:aspect-[2/1] object-cover origin-right scale-[1.01]"
          autoPlay
          muted
          loop
          playsInline
        />
      </section>
      <section
        id="banner"
        className="pt-16 pb-16 flex justify-center items-center"
        style={{ minHeight: "741px" }}
      >
        <div className="container max-w-6xl mx-auto px-4">
          <div className="flex flex-col-reverse laptop:flex-row items-center gap-12">
            <div className="w-full laptop:w-1/2 text-center laptop:text-left">
              <h1 className="text-2xl laptop:text-5xl font-bold leading-tight">
                브랜드의 가치를 높이는
              </h1>
              <span className="text-2xl laptop:text-5xl font-bold highlight-red leading-tight space-x-3">
                더리얼 마케팅
              </span>

              <ul className="list-none mt-6 text-left">
                <li className="text-center laptop:text-left text-base laptop:text-lg text-gray-600 leading-relaxed">
                  고객의 브랜드 가치를 극대화하는
                </li>
                <li className="text-center laptop:text-left text-base laptop:text-lg text-gray-600 leading-relaxed">
                  차별화된 마케팅 전략을 제시하는
                </li>
                <li className="text-center laptop:text-left text-base laptop:text-lg text-gray-600 leading-relaxed">
                  지속 가능한 성장을 지원하는
                </li>
              </ul>

              <div className="mt-8 flex justify-center laptop:justify-start text-center gap-2 laptop:gap-4">
                <a
                  onClick={didShowMarketingContact}
                  className="bg-[#f82836] hover:bg-[#ff4f5b] text-white font-semibold px-3 laptop:px-6 py-3 rounded-lg shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
                >
                  디지털마케팅 도입 문의
                </a>

                <a
                  onClick={didShowIntroducePage}
                  className="bg-white hover:bg-[#f1f3f5] text-black px-3 laptop:px-6 py-3  font-semibold rounded-lg
                   border border-[#ff4f5b] cursor-pointer whitespace-nowrap"
                >
                  회사소개 보기
                </a>
              </div>
            </div>
            <motion.div
              className="w-full laptop:w-1/2 flex justify-center laptop:justify-end space-x-6"
              variants={bannerVariants}
              whileHover={{ scale: 1.05 }}
            >
              <img
                src="/assets/images/marketing_banner_introduce.png"
                className="object-cover rounded-lg w-[400px]"
              />
            </motion.div>
          </div>
        </div>
      </section>
      <FadeInOnScroll>
        <section id="about" className="py-10 laptop:py-20 bg-[#fff4f5]">
          <div className="text-center px-4 md:px-8 lg:px-12">
            <h1 className="text-2xl laptop:text-4xl font-extrabold text-[#f82836]">
              이런 고민을 하고 계신가요?
            </h1>
            <h2 className="font-bold text-xl laptop:text-2xl pt-10 text-black max-w-[375px]:text-base max-w-[370px]:leading-relaxed">
              빠르게 바뀌는 트렌드,
              <br className="block laptop:hidden" />
              따라가기 벅차지 않으신가요?
            </h2>
            <p className="font-light text-pretty text-[12px] laptop:text-base pt-3 whitespace-pre-line  text-gray-800 max-w-4xl mx-auto">
              {`단기 성과에 집중하다 보면,\n고객들에게 브랜드 차별화된 메시지를 만들기 어렵습니다.\n 우리는 기획 단계부터 철학과 방향성을 반영해 IMC 전략을 설계하며, 일관된 브랜딩을 위해 구조화된 솔루션을 제공합니다.`}
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      <section id="audiomarketing" className="py-10 laptop:py-20 bg-[#f8f9fa] font-sans">
        <FadeInOnScroll>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-[40px] text-black mb-[48px] font-extrabold">
                <span className="font-extrabold text-[40px] text-[#f82836] pb-10">
                  영상
                </span>
                광고
              </h2>
            </div>
          </div>
          <MarketingMediaView
            imageUrls={MarketingInfo.imageUrls}
            scrollRef={scrollRef}
            titles={MarketingInfo.titles}
            hrefs={MarketingInfo.hrefs}
          />
        </FadeInOnScroll>
      </section>
      <section className="py-10 laptop:py-20 bg-[#fff4f5]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-[#f82836] mb-2">
              클라이언트
            </h2>
          </div>
          <MarketingClientView imageUrls={MarketingClientInfo.imageUrls} />
        </div>
      </section>

      <section id="history" className="py-10 laptop:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 justify-center">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-[#f82836] mb-3">
              THE REAL MARKETING
            </h2>
            <div className="w-24 h-1 bg-[#f82836] mx-auto mb-6"></div>
            <p className="text-xl text-gray-800 mb-2">더리얼 마케팅 연혁</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {timelineData.map((item) => (
              <button
                key={item.period}
                onClick={() => setSelectedYear(item.period)}
                className={`px-6 py-2 font-semibold transition-all duration-300 border-2 ${selectedYear === item.period ? `bg-red-500 text-white border-red-500` : `bg-white text-gray-800 border-gray-200 hover:border-red-500 hover:text-red-500`}`}
              >
                {item.period}
              </button>
            ))}
          </div>
        </div>
        {selectedData && (
          <div className="container max-w-[1200px] mx-auto laptop:pr-8 laptop:px-4 justify-center">
            <div className="bg-white border border-gray-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl laptop:text-3xl font-bold text-gray-800">
                  {selectedData.period} 주요성과
                </h2>
              </div>
              <div className="grid gap-4">
                {selectedData.events.map((event, index) => (
                  <div
                    key={index}
                    className={`p-4 border-l-4 transition-all duration-300 hover:shadow-md ${getCategoryBolder(event.category || "")}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 ${getCategoryBolder(event.category || "")}`}
                      >
                        {getCategoryIcon(event.category || "")}
                      </div>
                      <span className="text-gray-800 font-medium whitespace-pre-wrap">
                        {event.text}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <FooterView />
    </div>
  );
};

export default Marketingpage;
